import React, { useEffect, useState } from 'react'
import FormField from '@adsk/alloy-react-form-field'
import {
	AutocompleteControl,
	type GenericItem,
	Menu,
	MenuItem,
	useAutocomplete,
} from '@adsk/alloy-react-dropdown'
import { HighZIndexInfoPopOver } from '@adsk/napi-shared'
import Theme from '@adsk/alloy-react-theme'
import { useTranslation } from '../../../i18n'
import { track } from '@adsk/acs-web-analytics'
import ANALYTICS_TYPES from '../../types/AnalyticsTypes'
import { setUserInfo } from '../../store/actions'
import {
	OnboardingUserAppContextType,
	useOnboardingUserContext,
} from '../../store'
import { GetSelectionListByNameResult } from '@adsk/napi-api-client'
import { getPackageInfo } from '../../utils'
import _ from 'lodash'
import { SELECTION_LIST_KEYS } from '../../constants'
import styled from 'styled-components'

interface JobSelectionComponentProps {
	selectionsList: GetSelectionListByNameResult[]
}

const StyledInfoPopOver = styled(HighZIndexInfoPopOver)<{
	colors: typeof Theme.colors
}>`
	margin: auto auto auto 0.2rem;
	font-weight: bold;
	svg {
		fill: ${({ colors }) => colors.charcoal700};
	}
`
const JobSelectionComponent: React.FC<JobSelectionComponentProps> = ({
	selectionsList,
}: JobSelectionComponentProps): JSX.Element => {
	const { name, version } = getPackageInfo()
	const {
		dispatch,
		state: { userInfo, flowType },
	} = useOnboardingUserContext() as OnboardingUserAppContextType
	const [searchText, setSearchText] = useState<string>(userInfo?.title || '')
	const [selectedJobTitleItem, setSelectedJobTitleItem] =
		useState<GenericItem | null>(null)

	const { typography, colors } = Theme
	const { t } = useTranslation()
	const [selectionListItems] =
		useState<GetSelectionListByNameResult[]>(selectionsList)
	const [jobTitleSelectionList, setJobTitleSelectionList] = useState<
		GenericItem[]
	>([])

	const searchItem = searchText && {
		value: searchText,
		label: searchText,
	}

	const displayItems = searchItem
		? [searchItem, ...jobTitleSelectionList]
		: jobTitleSelectionList
	const {
		controlProps,
		menuProps: allMenuProps,
		reset,
	} = useAutocomplete({
		items: displayItems,
		inputValue: searchText,
		onInputValueChange: ({ inputValue }) => {
			setSearchText(inputValue ?? '')
		},
		onSelectedItemChange: ({ selectedItem }): void => {
			track(ANALYTICS_TYPES.NAPI_USERS_MODIFIED_MARKETING_INFO, {
				package: name,
				version,
				flowType,
				fieldName: 'jobTitle',
			})
			setSelectedJobTitleItem(selectedItem ?? null)
			dispatch(
				setUserInfo({
					...userInfo,
					title: selectedItem?.value as unknown as string,
				})
			)
		},
		selectedItem: selectedJobTitleItem,
	})
	const { menuItems, getItemProps, ...menuProps } = allMenuProps
	const { getInputProps } = controlProps

	const extendInputProps = (...args: any): any => ({
		...getInputProps(...args),
		placeholder: t('JOB_TITLE_PLACEHOLDER_TXT'),
		spellCheck: false,
		input: searchText,
	})

	const resetJobTitleSearchBox = () => {
		setSearchText('')
		dispatch(
			setUserInfo({
				...userInfo,
				title: '',
			})
		)
		reset()
	}

	const lockedFields = _.get(userInfo, 'lockedFields', []) as string[]

	useEffect(() => {
		selectionListItems?.forEach((constantList) => {
			if (constantList.name === SELECTION_LIST_KEYS.JOB_TITLES) {
				const jobTitleSelectionListItems = (constantList.optionsList ||
					[]) as unknown as GenericItem[]

				setJobTitleSelectionList(jobTitleSelectionListItems)
			}
		})
	}, [])

	useEffect(() => {
		setSelectedJobTitleItem(
			displayItems.find((item) => item.value === userInfo?.title) ?? null
		)
	}, [])

	const Footer = () => {
		return (
			<div
				data-testid={'jobTitleFooter'}
				style={{ borderTop: `1px solid ${colors.charcoal100}` }}
			>
				<div
					onClick={resetJobTitleSearchBox}
					style={{
						padding: '1rem',
						cursor: 'pointer',
						color: colors.adskBlue700,
						textAlign: 'right',
					}}
					onKeyDown={resetJobTitleSearchBox}
					role="link"
					tabIndex={0}
				>
					{t('CLEAR')}
				</div>
			</div>
		)
	}

	return (
		<FormField
			label={t('JOB_TITLE')}
			required={true}
			data-testid={'jobTitleLabelTestId'}
			labelVariant={FormField.LABEL_VARIANTS.TOP}
			style={typography.bodySmall}
			renderInfoPopover={() => {
				return (
					<StyledInfoPopOver
						colors={colors}
						message={
							<div data-testid="napi-onboarding-user-jobtitle-tootip-msg">
								{t('JOB_TITLE_TOOLTIP')}
							</div>
						}
					/>
				)
			}}
		>
			<AutocompleteControl
				{...controlProps}
				getInputProps={extendInputProps}
				data-testid={'jobTitleId'}
				placeholder={t('JOB_TITLE_PLACEHOLDER_TXT')}
				disabled={lockedFields.includes('title')}
				style={{
					background: colors.white,
					overflow: 'auto',
				}}
			/>
			<Menu {...menuProps} renderFooter={Footer}>
				{menuItems.map((item, index) => (
					<MenuItem
						key={item.value}
						{...getItemProps({
							item,
							index,
						})}
						isSelected={item.value === selectedJobTitleItem?.value}
					>
						{item.value}
					</MenuItem>
				))}
			</Menu>
		</FormField>
	)
}

export default JobSelectionComponent
